import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { openConsentPreferences } from '../lib/consent';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-dark text-white mt-24" data-testid="site-footer">
      <div className="container-page py-16">
        <div className="grid gap-12 md:grid-cols-12">
          {/* Brand */}
          <div className="md:col-span-5 space-y-5">
            <Link to="/" className="inline-flex items-center">
              <img
                src="/brand/agencive/transparent/logo-stacked-white-transparent.png"
                alt="Agencive"
                width="170"
                height="52"
                className="h-12 md:h-14 w-auto max-w-[170px] object-contain"
                style={{ objectFit: 'contain', height: 'auto' }}
                decoding="async"
                loading="lazy"
              />
            </Link>
            <p className="text-white/75 max-w-sm text-[15px] leading-relaxed">
              {t.footer.tagline}. Suomalainen brändiagenttistudio Pirkanmaalta.
            </p>
            <div className="flex gap-3 pt-2">
              <a
                href="https://www.tiktok.com/@agencive"
                target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-pill border border-white/15 hover:border-white/50 flex items-center justify-center transition-colors"
                aria-label="TikTok"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/agencive.ai/"
                target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-pill border border-white/15 hover:border-white/50 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Nav columns */}
          <div className="md:col-span-3">
            <h3 className="text-eyebrow uppercase text-white/60 mb-4">Navigointi</h3>
            <ul className="space-y-2.5 text-[15px]">
              <li><Link to="/" className="text-white/85 hover:text-white">{t.nav.home}</Link></li>
              <li><Link to="/palvelut" className="text-white/85 hover:text-white">{t.nav.services}</Link></li>
              <li><Link to="/meista" className="text-white/85 hover:text-white">{t.nav.about}</Link></li>
              <li><Link to="/yhteys" className="text-white/85 hover:text-white">{t.nav.contact}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="text-eyebrow uppercase text-white/60 mb-4">Yhteystiedot</h3>
            <ul className="space-y-2.5 text-[15px]">
              <li><a href="mailto:agencive.ai@gmail.com" className="text-white/85 hover:text-white">agencive.ai@gmail.com</a></li>
              <li><a href="tel:+358401601975" className="text-white/85 hover:text-white">+358 40 1601975</a></li>
              <li><a href="tel:+358449766413" className="text-white/85 hover:text-white">+358 44 9766413</a></li>
              <li className="text-white/70">Lempäälä, Pirkanmaa</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-white/65 text-[13.5px]">
            © {currentYear} Agencive AI Oy. {t.footer.rights}.
          </p>
          <div className="flex gap-6 text-[13.5px] text-white/75">
            <Link to="/tietosuoja" className="hover:text-white">Tietosuoja</Link>
            <Link to="/kayttoehdot" className="hover:text-white">Käyttöehdot</Link>
            <Link to="/ukk" className="hover:text-white">UKK</Link>
            <button
              type="button"
              onClick={openConsentPreferences}
              className="hover:text-white underline-offset-4 hover:underline"
              data-testid="footer-cookie-preferences"
            >
              Evästeasetukset
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
